import { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import PlayingCard from "./PlayingCard";
import { Check, X, ArrowUp, ArrowDown, Coins, GripHorizontal, MessageCircle } from "lucide-react";

/**
 * Speech bubble style incoming trade offer
 * Design: Dark theme with subtle blue accents for incoming offers
 */
export default function IncomingTradeBubble({
  trade,
  senderPosition, // "top" | "left" | "right" for positioning the bubble tail
  stackIndex = 0,
  onAccept,
  onReject,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
  const windowRef = useRef(null);

  // Calculate initial position based on sender position
  useEffect(() => {
    const baseOffset = stackIndex * 20;
    let initialPos = { x: 0, y: 0 };
    
    // Position near the bottom center (where my cards are) but offset based on sender
    const centerX = window.innerWidth / 2;
    const bottomY = window.innerHeight - 320;
    
    switch (senderPosition) {
      case "top": // Front player
        initialPos = { x: centerX - 120 + baseOffset, y: bottomY - 50 + baseOffset };
        break;
      case "left":
        initialPos = { x: 100 + baseOffset, y: bottomY + baseOffset };
        break;
      case "right":
        initialPos = { x: window.innerWidth - 300 - baseOffset, y: bottomY + baseOffset };
        break;
      default:
        initialPos = { x: centerX - 120 + baseOffset, y: bottomY + baseOffset };
    }
    
    setCurrentPos(initialPos);
  }, [senderPosition, stackIndex]);

  // Mouse/Touch handlers for dragging
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setDragOffset({
      x: clientX - currentPos.x,
      y: clientY - currentPos.y
    });
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    setCurrentPos({
      x: clientX - dragOffset.x,
      y: clientY - dragOffset.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, dragOffset]);

  // Determine bubble tail direction
  const getTailClass = () => {
    switch (senderPosition) {
      case "top":
        return "before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-8 before:border-transparent before:border-b-slate-800/95";
      case "left":
        return "before:absolute before:top-4 before:-left-2 before:border-8 before:border-transparent before:border-r-slate-800/95";
      case "right":
        return "before:absolute before:top-4 before:-right-2 before:border-8 before:border-transparent before:border-l-slate-800/95";
      default:
        return "";
    }
  };

  return (
    <div
      ref={windowRef}
      className={`fixed z-30 w-56 bg-slate-800/95 backdrop-blur-sm border border-sky-900/40 rounded-xl shadow-lg ${getTailClass()}`}
      style={{
        left: currentPos.x,
        top: currentPos.y,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {/* Drag Handle with sender info */}
      <div
        className="flex items-center justify-between px-2 py-1.5 bg-sky-900/20 rounded-t-xl cursor-grab active:cursor-grabbing border-b border-sky-900/20"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className="flex items-center gap-1.5">
          <MessageCircle className="w-3 h-3 text-sky-400/70" />
          <span className="text-xs font-medium text-sky-200">{trade.from_nickname}</span>
        </div>
        <GripHorizontal className="w-3 h-3 text-slate-500" />
      </div>

      {/* Content */}
      <div className="p-2 space-y-1.5">
        {/* What they're offering */}
        <div className="text-[10px] text-emerald-400/70 font-medium">くれるもの:</div>
        <div className="flex flex-wrap gap-1 items-center min-h-[24px]">
          {trade.offer_cards_data?.length > 0 && trade.offer_cards_data.map((card, i) => (
            <PlayingCard key={i} suit={card.suit} rank={card.rank} isPublic={true} size="xxs" />
          ))}
          {trade.offer_chips > 0 && (
            <div className="flex items-center gap-0.5 text-[10px] text-emerald-300/80 bg-emerald-900/20 px-1.5 py-0.5 rounded">
              <Coins className="w-2.5 h-2.5 text-amber-500/70" />
              <span>+{trade.offer_chips}</span>
            </div>
          )}
          {trade.offer_cards_data?.length === 0 && trade.offer_chips === 0 && (
            <span className="text-[10px] text-slate-500">なし</span>
          )}
        </div>

        {/* What they want */}
        <div className="text-[10px] text-red-400/70 font-medium">欲しいもの:</div>
        <div className="flex flex-wrap gap-1 items-center min-h-[24px]">
          {trade.request_cards_data?.length > 0 && trade.request_cards_data.map((card, i) => (
            <PlayingCard key={i} suit={card.suit} rank={card.rank} isPublic={true} size="xxs" />
          ))}
          {trade.request_chips > 0 && (
            <div className="flex items-center gap-0.5 text-[10px] text-red-300/80 bg-red-900/20 px-1.5 py-0.5 rounded">
              <Coins className="w-2.5 h-2.5 text-amber-500/70" />
              <span>-{trade.request_chips}</span>
            </div>
          )}
          {trade.request_cards_data?.length === 0 && trade.request_chips === 0 && (
            <span className="text-[10px] text-slate-500">なし</span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-2 pb-2 flex gap-1.5">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onReject(trade.id)}
          className="flex-1 h-6 text-[10px] text-red-400/80 hover:text-red-300 hover:bg-red-900/20 border border-red-900/30"
        >
          <X className="w-3 h-3 mr-0.5" />
          拒否
        </Button>
        <Button
          size="sm"
          onClick={() => onAccept(trade.id)}
          className="flex-1 h-6 text-[10px] bg-emerald-700/60 hover:bg-emerald-600/60 text-emerald-100 border border-emerald-600/30"
        >
          <Check className="w-3 h-3 mr-0.5" />
          受諾
        </Button>
      </div>
    </div>
  );
}
